import { AREA_LAYER_MAX_ZOOM, AREA_LAYER_MIN_ZOOM } from '@/wabunka-src/common/features/Experience/core/areaMarkerLayer/areaMarkerLayer';

/**
 * 一覧マップの「どの層を描くか」の状態機械（JCAT-5434）。
 *
 * この判断は Google Maps のイベント（ズーム・ドラッグ・移動完了）と、自前の自動フィット、
 * 描画結果（実際にドットを描けたか）が絡み合う。地図コンポーネントの中に可変フラグとして
 * 散らばらせると、フラグどうしの噛み合わせが壊れても**動かして初めて分かる**状態になる。
 * 実際この機能ではその壊れ方を繰り返した（層に入れない／出られない／往復する／
 * カメラだけ動いて中身が出ない／取得失敗時に検索が止まる、など）。
 *
 * そこで**状態遷移だけを純粋関数として切り出し**、イベント列に対する不変条件をテストで固定する。
 * コンポーネント側は Google Maps のイベントをここのイベントに翻訳し、
 * セレクタが返す判断（層に留めるか・通知を止めるか・カメラを合わせるか）に従うだけにする。
 *
 * 設計の要点:
 *
 * - **層はユーザー操作でだけ切り替わるラッチ**。現在のズームから毎回導出すると、自分で仕掛けた
 *   自動フィットの着地点で反転してしまう
 * - **基準の画角より引いていること**を層に入る条件に課す。これが無いと、一度寄ってから同じ画角に
 *   戻したときや 0 件リセット後にズームインしたときに広域表示になり、操作と結果が逆向きになる
 * - **基準の画角は実寸のあるペインで初回フィットできてから**確定する。0x0 のコンテナ
 *   （SP はリスト表示のあいだ地図が hidden）で走ったフィットの値で固定すると、層に入れなくなる
 * - **カメラ移動と検索通知の停止は「実際に描けてから」**。ズーム判定だけで行うと、データ取得に
 *   失敗したときにカメラだけ日本全体へ飛び、ラベルも出ず検索も止まったまま復帰しなくなる
 * - **層への入り方は 2 種類あり、カメラと検索の扱いが違う**。広域ズーム由来と「視野に体験が無い」
 *   由来を区別する（`AreaLayerReason`）。後者でカメラを動かすと見ていた場所を奪い、
 *   検索を止めると体験のある場所へパンで戻れなくなる
 */

/**
 * エリアラベル層に入っている理由。**入り方によってカメラと検索の扱いが変わる**ため区別する。
 *
 * - `wide` … ユーザーが広域まで引いた。日本全体へカメラを合わせ、検索のビューポート連動は止める
 *   （合わせた bbox が検索へ流れると左リストが全国に差し替わるため）
 * - `empty` … 視野内に体験が 1 件も無い。カメラは動かさず（ユーザーが見ている場所を奪わない）、
 *   検索のビューポート連動も**止めない**（止めると体験のある場所へパンで戻れなくなる）
 */
export type AreaLayerReason = 'wide' | 'empty';

export interface MapLayerState {
  /** ユーザーが自分で地図を動かしたか（ズーム操作・ドラッグ）。false の間が「初期表示」。 */
  hasUserMovedMap: boolean;
  /** 自分で仕掛けたズームの最中か。ここでのズーム変化はユーザー操作と数えない。 */
  isAutoZooming: boolean;
  /** エリアラベル層に入っているか。ユーザーのズーム操作でだけ切り替わる。 */
  isAreaLayerActive: boolean;
  /** 「ここより引いたらエリアラベル層」の基準になる画角。未確定なら null。 */
  referenceZoom: number | null;
  /** 全ピンへの初回フィットが走ったか。 */
  hasFittedPins: boolean;
  /** その初回フィットが実寸のあるペインで走ったか。false なら結果は当てにならない。 */
  hasFittedWithMeasuredPane: boolean;
  /** 視野内に体験ピンがあるか。無ければ（＝行き止まり）エリアへ誘導する。 */
  hasExperiencePinsInView: boolean;
  /**
   * 上のピン情報が古いか。広域層のあいだは検索のビューポート連動を止めているため、
   * 層を抜けた直後のピンは**ズームアウトする前の場所のもの**で、今の視野の答えになっていない。
   */
  hasStalePinData: boolean;
  /**
   * 今の層セッションで一度でもエリアを描けたか。
   *
   * **理由そのものは持たない。** 保持すると、描けたまま理由だけが変わったとき
   * （空由来で描いている最中にズームアウトする等）に確定し直す機会が無い。描画状態の変化は
   * 「変わったとき」しか届かないため、保持した理由は畳まれたまま二度と埋まらない。
   */
  hasEngagedAreaLayer: boolean;
}

export type MapLayerEvent =
  /** これから自分でズームを変える。 */
  | { type: 'autoZoomStarted' }
  /** 全ピンへのフィットを実行した。 */
  | { type: 'pinsFitted'; hasMeasuredPane: boolean }
  /** 地図のズームが変わった。 */
  | { type: 'zoomChanged'; zoom: number | null }
  /** ユーザーが地図をドラッグし始めた。 */
  | { type: 'mapDragged'; zoom: number | null }
  /** 地図の移動が完了した（idle）。 */
  | { type: 'moveSettled'; zoom: number | null }
  /** エリアを実際に描けているかが変わった。 */
  | { type: 'areaRenderedChanged'; rendered: boolean }
  /** 視野内の体験ピンの有無が変わった。 */
  | { type: 'experiencePinsChanged'; hasPins: boolean }
  /** 0 件表示から初期表示へ戻した。 */
  | { type: 'viewReset' };

export const initialMapLayerState: MapLayerState = {
  hasUserMovedMap: false,
  isAutoZooming: false,
  isAreaLayerActive: false,
  referenceZoom: null,
  hasFittedPins: false,
  hasFittedWithMeasuredPane: false,
  // 取得前に「0 件だから行き止まり」と誤判定しないよう、あるものとして始める
  hasExperiencePinsInView: true,
  hasStalePinData: false,
  hasEngagedAreaLayer: false,
};

/**
 * いまエリアラベル層に入るべきか、入るならどの理由か。
 *
 * 広域ズームを優先する。両方成立している（引いていて、かつ視野に体験が無い）場合は
 * 日本全体を俯瞰したいはずなので、カメラを合わせる `wide` として扱う。
 */
export const areaLayerReason = (state: MapLayerState): AreaLayerReason | null => {
  if (state.isAreaLayerActive) return 'wide';
  // 古い情報で「体験がある」と判断してピン層へ落ちない。落とすと、再取得で 0 件と分かった時点で
  // エリア層へ戻ることになり、スクリムとラベルが消えて出直す二度手間が見える
  if (!state.hasExperiencePinsInView || state.hasStalePinData) return 'empty';
  return null;
};

/**
 * いま層を「始められている」理由。描けていなければ null。
 *
 * 理由は毎回導出する。カメラ移動と検索通知の扱いはこれで決まるので、描けたまま理由だけが
 * 変わった場合（空由来で描いている最中にズームアウトする等）も追従する必要がある。
 */
export const engagedAreaLayerReason = (state: MapLayerState): AreaLayerReason | null => (state.hasEngagedAreaLayer ? areaLayerReason(state) : null);

/**
 * 層を出たときにセッションを畳む。層の中にいる限りは畳まない
 * （画面内の可視ドットが 0 になる位置へパンしただけで検索通知が復活しないようにするため）。
 */
const syncLayerSession = (previous: MapLayerState, updated: MapLayerState): MapLayerState => {
  // 広域層を抜けた時点で、ピン情報は古いものになる（層のあいだ再取得を止めていたため）。
  // 次にピンが届くまでは、その情報を層の判断に使わない。
  // 理由は保持せず毎回導出するので、**更新前の状態**と突き合わせないと「抜けた」ことを判定できない。
  const leftWideSession = engagedAreaLayerReason(previous) === 'wide' && engagedAreaLayerReason(updated) !== 'wide';
  const next = leftWideSession ? { ...updated, hasStalePinData: true } : updated;

  return areaLayerReason(next) === null ? { ...next, hasEngagedAreaLayer: false } : next;
};

/** ユーザーのズーム操作の結果、エリアラベル層に入るか。 */
const entersAreaLayer = (zoom: number | null, referenceZoom: number | null): boolean => {
  if (zoom == null || !Number.isFinite(zoom)) return false;
  if (Math.floor(zoom) > AREA_LAYER_MAX_ZOOM) return false;
  // 基準が未確定なら閾値だけで判断する（ピン 0 件で初回フィットが走らない検索など）
  return referenceZoom == null || zoom < referenceZoom;
};

export const nextMapLayerState = (state: MapLayerState, event: MapLayerEvent): MapLayerState => {
  switch (event.type) {
    case 'autoZoomStarted':
      return { ...state, isAutoZooming: true };

    case 'pinsFitted':
      // フィットし直したら基準も取り直す（次の移動完了で確定する）
      return { ...state, isAutoZooming: true, hasFittedPins: true, hasFittedWithMeasuredPane: event.hasMeasuredPane, referenceZoom: null };

    case 'zoomChanged': {
      if (state.isAutoZooming) return state;
      return syncLayerSession(state, { ...state, hasUserMovedMap: true, isAreaLayerActive: entersAreaLayer(event.zoom, state.referenceZoom) });
    }

    case 'mapDragged': {
      // ドラッグでも層に**入れる**。初回フィットが最小ズームに張り付くと（沖縄〜北海道に散る検索など）
      // それ以上ズームアウトできず `zoom_changed` が発火しないため、ズーム操作だけを起点にすると
      // そのセッションで層に一度も入れない。通常の画角では基準の条件を満たさないので、
      // 「パンしただけで層に入る」ことにはならない。
      //
      // ただし**出る方向には効かせない**。ドラッグでズームから再導出すると、層に入ったあとの
      // 自動フィット（日本全体フィット）の着地点が基準以上だった場合、俯瞰層で普通にパンしただけで
      // 層から蹴り出される。ラッチにした意味がドラッグ経路から破られるため、入る方向だけにする。
      //
      // 自動ズームの最中も層は動かさない。リセット直後（基準が未確定・自動ズーム中）にパンすると
      // 閾値だけで判定して層に入ってしまい、「Tap to view all experiences」を押した直後に体験が消える。
      if (state.isAutoZooming) return { ...state, hasUserMovedMap: true };
      return syncLayerSession(state, {
        ...state,
        hasUserMovedMap: true,
        isAreaLayerActive: state.isAreaLayerActive || entersAreaLayer(event.zoom, state.referenceZoom),
      });
    }

    case 'moveSettled': {
      const settled = { ...state, isAutoZooming: false };
      // 実寸のあるペインで初回フィットが済むまで基準を確定しない
      if (settled.referenceZoom != null || !settled.hasFittedPins || !settled.hasFittedWithMeasuredPane) return settled;
      if (event.zoom == null || !Number.isFinite(event.zoom)) return settled;
      // 層に到達できなくなると機能が死ぬので、最小ズームより 1 段は引ける値に丸める
      return { ...settled, referenceZoom: Math.max(event.zoom, AREA_LAYER_MIN_ZOOM + 1) };
    }

    case 'areaRenderedChanged':
      // 描けなくなっても畳まない。層の中で可視ドットが 0 になる位置へパンしただけで
      // 検索通知が復活すると、左リストがその範囲に差し替わる
      if (!event.rendered || state.hasEngagedAreaLayer || areaLayerReason(state) === null) return state;
      return { ...state, hasEngagedAreaLayer: true };

    case 'experiencePinsChanged':
      // 値が同じでも「届いた」こと自体に意味がある（古さが解ける）ので早期 return しない
      if (state.hasExperiencePinsInView === event.hasPins && !state.hasStalePinData) return state;
      return syncLayerSession(state, { ...state, hasExperiencePinsInView: event.hasPins, hasStalePinData: false });

    case 'viewReset':
      return {
        ...initialMapLayerState,
        hasFittedPins: state.hasFittedPins,
        hasFittedWithMeasuredPane: state.hasFittedWithMeasuredPane,
        // 体験の有無は初期値（あるものとして）に戻す。これから bbox を広げて取り直すので、
        // 取り直す前の 0 件を引き継ぐと「全件表示に戻す」ボタンがエリア層に着地してしまう
        isAutoZooming: true,
      };
  }
};

// ===== セレクタ（状態から判断を導く。副作用は呼び出し側） =====

/**
 * 体験ピン層に留めるべきか。
 *
 * 広域まで引いていない、かつ視野内に体験がある間は体験ピン層。初期表示のうちは
 * 引いていても層に入らない（開いた瞬間に地図から体験が消えないようにするため）。
 */
export const forcesPinLayer = (state: MapLayerState): boolean => {
  if (!state.hasUserMovedMap && state.hasExperiencePinsInView) return true;
  return areaLayerReason(state) === null;
};

/**
 * 検索のビューポート通知を止めるべきか。
 *
 * 「実際に層を始められた」ときだけ止める。ズーム判定だけで止めると、データ取得に失敗したときに
 * 検索が二度と更新されなくなる。一方で描画状態そのもので判断すると、層の中で可視ドットが 0 になる
 * 位置へパンしただけで通知が復活し、左リストがその範囲に差し替わる。
 */
export const suppressesViewportNotification = (state: MapLayerState): boolean => engagedAreaLayerReason(state) === 'wide';

/**
 * エリアデータを先読みすべきか。
 *
 * **地図を触ったら取りに行く。ズームの帯では絞らない。**
 *
 * 層には広域ズームだけでなく「視野に体験が無い」でも入る（`AreaLayerReason`）。後者は狭域でも
 * 起きるので、閾値の帯で絞ると**視野が空になってから取得が始まる**。ピンの再取得の往復に
 * エリア取得の往復が直列に積まれ、体験が消えてからラベルが出るまでが目に見えて空く。
 *
 * 地図を一度も触らないユーザーには払わせない（D-007）という当初の意図はそのまま残る。
 * ラッチを条件に混ぜないことも変わらない。ラッチは層の内側でしか立たないため、混ぜると
 * 「層に入る前に用意しておく」ことが構造的にできなくなる。
 *
 * 「視野に体験が無い」だけでは取りに行かない。**初回フィットが走っていること**を課す。
 * 座標付き体験が 0 件のページはマップペインを CSS で隠すだけでアンマウントしないため、
 * ピン 0 件の確定だけを条件にすると、マップを一度も描かないページで 3 本が走る。
 * フィットはピンが 1 件以上ないと走らないので、これで除外できる。
 */
export const shouldPrefetchAreaData = (state: MapLayerState): boolean => state.hasUserMovedMap || (state.hasFittedPins && !state.hasExperiencePinsInView);

/** 0x0 のコンテナで走ったフィットをやり直すべきか。 */
export const shouldRefitPins = (state: MapLayerState, hasMeasuredPane: boolean): boolean => state.hasFittedPins && !state.hasFittedWithMeasuredPane && hasMeasuredPane;

/**
 * 日本全体へカメラを合わせるべきタイミングか。
 *
 * **広域ズームで入ったときだけ。** 視野が空だから入ったとき（`empty`）に動かすと、
 * ユーザーが見ていた場所を奪ってしまう（大阪を見ているのに日本全体へ飛ぶ）。
 */
export const startsAreaLayerSession = (previous: MapLayerState, next: MapLayerState): boolean => engagedAreaLayerReason(previous) !== 'wide' && engagedAreaLayerReason(next) === 'wide';
